import type ApplicationInstance from '@ember/application/instance';
import { registerDestructor } from '@ember/destroyable';
import { installAuthContextProviders, resetAuthContext } from 'tio-data/auth-context';
import { installAppContextProviders, resetAppContext } from 'tio-data/app-context';
import { onRequestEvent, type TioRequestEvent } from 'tio-data/request-events';
import { addError } from 'tio-common/utils/datadog';
import type TelemetryService from 'tio-common/services/telemetry';
import type SessionService from 'tio-employee/services/session';
import type SessionContextService from 'tio-employee/services/session-context';
import type ImpersonationService from 'tio-employee/services/impersonation';
import type DemoModeService from 'tio-employee/services/demo-mode';
import type NotificationsService from 'tio-ui/services/notifications';
import { resolveDemoScenario } from 'tio-employee/demo/registry';

type RenewableSession = SessionService & {
  expireAt?: number;
  renew?: (token?: string) => Promise<unknown> | unknown;
};

/**
 * ADR-001: the app shell installs the request-layer's app-scoped providers
 * into stable containers at BOOT (before any route runs), so every consumer —
 * the request-manager chain AND the raw-fetch sites using buildTioAuthHeaders
 * (e.g. PreRegisterService#load, which runs in the application route's
 * `model()` hook before the store service is necessarily instantiated) — reads
 * live state regardless of construction order.
 *
 * This initializer owns, for tio-employee:
 *  - auth-context providers (token/publicKey/renewal/userId) — the renew +
 *    userId set tio-employee keeps over the agent baseline;
 *  - app-context providers (impersonation gate + demo interception), which the
 *    module-scope store factory references through stable delegates — they need
 *    owner lookups that only exist once an ApplicationInstance boots;
 *  - the request-event subscription (employee's richer handler: logout on
 *    `unauthorized`, toast + Datadog on `request-failed`), moved here from the
 *    store constructor at the wave-2 store-factory flip.
 *
 * Previously the auth providers were installed in the store constructor, so a
 * raw fetch that fired before the store was first touched saw an empty
 * auth-context and dropped `tio-auth-token` (the /preregister/employee delta).
 */
export function initialize(appInstance: ApplicationInstance): void {
  const session = () => appInstance.lookup('service:session') as RenewableSession | undefined;
  const sessionContext = () =>
    appInstance.lookup('service:session-context') as SessionContextService | undefined;
  const telemetry = () => appInstance.lookup('service:telemetry') as TelemetryService | undefined;
  const impersonation = () =>
    appInstance.lookup('service:impersonation') as ImpersonationService | undefined;
  const demoMode = () => appInstance.lookup('service:demo-mode') as DemoModeService | undefined;

  installAuthContextProviders({
    token: () => session()?.data?.authenticated?.access_token,
    isAuthenticated: () => session()?.isAuthenticated ?? false,
    publicKey: () => session()?.publicKey,
    sessionExpiresAt: () => session()?.expireAt,
    renew: (token?: string) => session()?.renew?.(token),
    userId: () => sessionContext()?.user?.id as string | undefined,
  });

  installAppContextProviders({
    impersonation: {
      isImpersonated: () => impersonation()?.isImpersonated ?? false,
      confirmMutation: () => {
        const svc = impersonation();
        return svc ? svc.confirmMutation() : Promise.resolve(true);
      },
    },
    demo: {
      isActive: () => demoMode()?.isActive ?? false,
      resolve: () => {
        const svc = demoMode();
        return svc?.isActive ? resolveDemoScenario(svc.currentSelection) : null;
      },
    },
  });

  // Map request-events to tio-employee's session behavior: `unauthorized`
  // (session-fatal status) logs out via session-context; `request-failed`
  // surfaces a toast and reports to Datadog. Mirrors the pre-flip store
  // constructor's onRequestEvent handler.
  const unsubscribe = onRequestEvent((event: TioRequestEvent) => {
    const userId = event.userId ?? 'unknown';
    telemetry()?.trackRequest({
      status: event.status,
      method: event.method,
      url: event.url,
      duration: event.duration,
    });
    if (event.type === 'unauthorized') {
      addError(`TioResponseHandler caught error status ${event.status} for user ${userId}`, {
        status: event.status,
        url: event.url,
        method: event.method,
        payload: event.payload,
        userId: event.userId,
      });
      void sessionContext()?.logout();
    } else if (event.type === 'request-failed') {
      try {
        (appInstance.lookup('service:notifications') as NotificationsService | undefined)?.add(
          event.message,
          { appearance: 'error' }
        );
      } catch (toastErr) {
        console.error('Failed to show error notification:', toastErr, 'Original:', event.message);
      }
      addError(
        `TioResponseHandler caught non-fatal error status ${event.status} for user ${userId}`,
        {
          status: event.status,
          url: event.url,
          method: event.method,
          payload: event.payload,
          userId: event.userId,
          errorMessage: event.message,
        }
      );
    }
  });

  registerDestructor(appInstance, () => {
    unsubscribe();
    resetAuthContext();
    resetAppContext();
  });
}

export default { initialize };
